import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Http from "../../Http";
import { Loan_API } from "../../Helper";

import { motion } from "framer-motion";
import {
  FaHome,
  FaBuilding,
  FaLandmark,
  FaRegHandshake,
  FaRedoAlt,
  FaExchangeAlt,
  FaUserNinja,
} from "react-icons/fa";

import {
  FaRupeeSign,
  FaChartLine,
  FaWallet,
  FaClock,
  FaPercentage,
  FaFileInvoice,
  FaHandHoldingUsd,
  FaShieldAlt,
} from "react-icons/fa";
import {
  BadgeCheck,
  User,
  Banknote,
  Home,
  CreditCard,
  Building2,
} from "lucide-react";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const HomeLoanNew = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openIndex, setOpenIndex] = useState(0); // First section open by default

  const faqs = [
    {
      question: "  What is a home loan?",
      answer:
        "A home loan, also known as a mortgage, is a financial arrangement where a lender provides funds to help you purchase or refinance a home. You agree to repay the loan over a specified period, typically with interest, through regular installments. The property serves as collateral, meaning the lender can foreclose on it if you default on the loan.",
    },
    {
      question: "What factors should I consider when choosing a home loan?",
      answer:
        "When selecting a home loan, consider factors like the interest rate (fixed or variable), loan term, monthly payment amount, total loan cost, and additional fees. It is also essential to evaluate your credit score, as it influences the terms and interest rates you qualify for. Additionally, consider other aspects such as down payment requirements, loan-to-value ratio, and any prepayment penalties.",
    },
    {
      question: "Which bank offers the lowest interest rate on home loans?",
      answer:
        "The interest rate for home loans depends on several factors, including the loan amount, your credit score, loan tenure, and financial history. To find the lowest rate, compare offers from different banks and lending institutions. Personalized rates can be obtained by providing your financial information to lenders and using our comparison tools to assess your best options.",
    },
    {
      question: "Can I get a home loan through TheFinMaster.com?",
      answer:
        " TheFinMaster.com does not offer home loans directly. Instead, we provide educational resources, loan comparison tools, and unbiased information to help you understand home loans and choose the best one for your needs. Once you have identified a suitable loan, you will be directed to the lenders website to complete a secure application process.",
    },
    {
      question: "What is the maximum tenure available for a home loan?",
      answer:
        " Home loans in India generally offer a tenure of up to 30 years. Opting for a longer tenure can lower your EMI but increases the total interest paid. Choose a tenure that aligns with your financial stability and future goals.",
    },
    {
      question: "How much loan amount can I get against my income?",
      answer:
        "Most lenders offer a home loan up to 75%–90% of the property value, depending on your income, repayment capacity, and credit profile. Some banks also consider co-applicant income to enhance loan eligibility.",
    },
    {
      question: "What is the difference between a fixed and floating interest rate?",
      answer:
        " A fixed interest rate remains constant throughout the tenure, ensuring stable EMIs. A floating rate fluctuates with market conditions, which may result in lower EMIs when rates fall, but could also increase when rates rise.",
    },
    {
      question: "What are the tax benefits available on home loans?",
      answer:
        "You can claim tax deductions under Section 80C for principal repayment (up to ₹1.5 lakh) and under Section 24(b) for interest paid (up to ₹2 lakh) annually, subject to conditions. These benefits apply only if the property is self-occupied or rented.",
    },
    {
      question: "Is it mandatory to have a co-applicant for a home loan?",
      answer:
        " A co-applicant is not mandatory but can help improve your loan eligibility. Usually, spouses or close family members are made co-applicants, especially when the property is jointly owned.",
    },
    {
      question: "Can I prepay or foreclose my home loan early?",
      answer:
        "Yes, most home loans can be prepaid or foreclosed without penalties, especially if the loan is on a floating rate. For fixed-rate loans, some banks may charge a small fee. Always check the terms before proceeding.",
    },
    {
      question: "What is a home loan processing fee?",
      answer:
        "This is a one-time charge levied by lenders for evaluating and processing your application. It usually ranges between 0.25% to 1% of the loan amount. Some banks may waive it as part of promotional offers.",
    },
    {
      question: "How long does it take for a home loan to be approved?",
      answer:
        "Home loan approval typically takes 5 to 10 working days after submitting complete documentation. Pre-approved borrowers or applicants with strong profiles may receive faster approval and disbursement.",
    },
    {
      question: "Can NRIs apply for a home loan in India?",
      answer:
        " Yes, many Indian banks offer home loans to Non-Resident Indians (NRIs). The eligibility, documentation, and repayment terms may slightly differ from those for resident Indians. Loan repayment must be done in Indian rupees.",
    },
    {
      question: "Do I need property insurance for a home loan?",
      answer:
        " Lenders often require property insurance to cover damages from natural disasters or accidents. This protects both the borrower and the bank’s financial interest in the property during the loan tenure.",
    },
    {
      question: "What is a home loan balance transfer?",
      answer:
        "A home loan balance transfer allows you to shift your existing loan to another bank offering a lower interest rate. It helps reduce EMI and interest burden, but also involves a fresh set of processing and documentation.",
    },
    {
      question: "What is a sanction letter in home loans?",
      answer:
        " A sanction letter is an official document from the bank stating the loan amount approved, interest rate, tenure, and other terms. It’s issued after the bank evaluates your profile and is valid for a limited time, usually 3 to 6 months.",
    },
    
  ];

  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };

  useEffect(() => {
    fetchLoanData();
  }, []);
  const fetchLoanData = async () => {
    try {
      const response = await Http.get(Loan_API);

      // The response structure might have nested data
      // Check if response has an insurance property (like in the original fetch response)
      const data = response.loan || response;

      // Filter the insurance data to show only Car category
      const carLoanData = Array.isArray(data)
        ? data.filter((insurance) => insurance.category_name === "Home Loan")
        : [];

      setLoanData(carLoanData);
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch Loan data");
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };
  const loanTypes = [
    {
      name: "New Home Loan 🏡",
      description:
        "A New Home Loan is designed for individuals purchasing a residential property for the first time. This loan helps homebuyers finance their dream house, with banks covering up to 90% of the property’s cost, based on eligibility.",
      icon: <FaHome />,
    },
    {
      name: "Home Purchase Loan 🏠",
      description:
        "This is a standard home loan offered to individuals looking to purchase a ready-to-move-in house, an under-construction property, or a resale home. The loan amount is disbursed directly to the property seller or builder.",
      icon: <FaBuilding />,
    },
    {
      name: "Home Construction Loan 🏗️",
      description:
        "For individuals who wish to build a home on their own land, a home construction loan provides the necessary funding. Unlike a standard home loan, the disbursal happens in phases as the construction progresses.",
      icon: <FaLandmark />,
    },
    {
      name: "Plot Loan (Land Loan) 🌍",
      description:
        "A Plot Loan helps borrowers purchase a piece of land for future construction. This type of loan is suitable for those who want to buy land and build their house later, subject to bank regulations regarding plot location and usage.",
      icon: <FaLandmark />,
    },
    {
      name: "Pre-Approved Home Loan ✅",
      description:
        "Financial institutions offer Pre-Approved Home Loans to eligible customers based on their income, credit score, and repayment capacity. These loans come with a pre-set limit and allow borrowers to finalize their property purchase within a given time frame.",
      icon: <FaRegHandshake />,
    },
    {
      name: "Home Loan Top-Up 💰",
      description:
        "Existing home loan borrowers can avail of a top-up loan to borrow additional funds over and above their current loan. These loans come with flexible end-use and lower interest rates compared to personal loans.",
      icon: <FaRedoAlt />,
    },
    {
      name: "Home Extension/Renovation Loan 🛠️",
      description:
        "Home Renovation or Extension Loans are designed for homeowners looking to repair, modify, or extend their existing homes. Banks provide these loans for remodeling, upgrading interiors, or even adding new rooms to the house.",
      icon: <FaHome />,
    },
    {
      name: "Balance Transfer Home Loan 🔄",
      description:
        "A Balance Transfer Home Loan allows borrowers to transfer their existing home loan from one lender to another, typically to avail of lower interest rates, better repayment terms, or improved service quality.",
      icon: <FaExchangeAlt />,
    },
    {
      name: "Home Conversion Loan 🏡➡️🏠",
      description:
        "This loan is ideal for individuals who have already purchased a home with a home loan but wish to move to a new property. The outstanding loan amount gets transferred to the new house, avoiding the need for a fresh home loan.",
      icon: <FaExchangeAlt />,
    },
    {
      name: "Home Loans for NRIs 🌍",
      description:
        "Banks and financial institutions offer specialized Home Loans for Non-Resident Indians (NRIs) who wish to invest in residential properties in India. These loans often come with different eligibility criteria and repayment terms compared to regular home loans.",
      icon: <FaUserNinja />,
    },
  ];

  const homeLoanComponents = [
    {
      title: "Loan Amount (Principal Amount)",
      description: `The principal amount is the total sum borrowed from the lender to purchase, construct, or renovate a home. It excludes interest charges but directly influences the overall repayment amount. The maximum loan amount granted depends on the applicant’s income, creditworthiness, and the lender’s loan-to-value (LTV) ratio policies.`,
      icon: <FaRupeeSign />,
    },
    {
      title: "Interest Rate",
      description: `The interest rate is the percentage charged by the lender on the borrowed amount. It significantly impacts the total repayment amount. Interest rates can be:
      
      • Fixed Rate: The interest remains constant throughout the loan tenure.
      • Floating Rate: The interest fluctuates based on market conditions and external benchmark rates like the RBI’s repo rate.`,
      icon: <FaChartLine />,
    },
    {
      title: "Equated Monthly Installments (EMI)",
      description: `EMI is the monthly repayment amount that borrowers must pay, consisting of both principal and interest. The EMI amount depends on the loan tenure, interest rate, and loan amount. A longer tenure results in lower EMIs but increases the overall interest paid.`,
      icon: <FaWallet />,
    },
    {
      title: "Loan Tenure",
      description: `The loan tenure is the period within which the borrower must repay the loan. Home loans typically come with long tenures ranging from 5 to 30 years, depending on the borrower’s preference and repayment capacity. Opting for a longer tenure reduces EMI but increases the total interest paid.`,
      icon: <FaClock />,
    },
    {
      title: "Down Payment & Loan-to-Value (LTV) Ratio",
      description: `Lenders generally finance up to 75-90% of the property’s value, while the remaining amount (10-25%) must be paid upfront by the borrower as a down payment. The LTV ratio determines the percentage of the property value financed by the lender. A lower LTV means a higher down payment but reduces the total loan burden.`,
      icon: <FaPercentage />,
    },
    {
      title: "Processing Fees & Other Charges",
      description: `Banks and financial institutions charge various fees during loan processing, including:
      
      • Processing Fees: A one-time fee (0.5% to 2% of the loan amount) for loan processing.
      • Prepayment Charges: Fees for early loan repayment (applicable only on fixed-rate loans).
      • Late Payment Fees: Charges for missed or delayed EMI payments.
      • Legal & Valuation Charges: Fees for property verification, legal documentation, and valuation reports.`,
      icon: <FaFileInvoice />,
    },
    {
      title: "Prepayment & Foreclosure Options",
      description: `Borrowers can prepay part of their loan or foreclose it entirely before the tenure ends. Prepayments reduce the outstanding principal and interest burden. While most floating-rate home loans have zero prepayment charges, fixed-rate loans may incur a penalty.`,
      icon: <FaHandHoldingUsd />,
    },
    {
      title: "Home Loan Insurance",
      description: `Many lenders offer home loan protection plans (HLPPs) that provide insurance coverage for loan repayment in case of unforeseen events like the borrower’s death or disability. This ensures financial security for the borrower’s family.`,
      icon: <FaShieldAlt />,
    },
  ];
  const points = [
    {
      title: " Affordable Interest Rates",
      description: `Home loans have lower interest rates compared to personal loans, making repayments more manageable.`,
    },
    {
      title: "Higher Loan Amount ",
      description: ` Borrowers can finance up to 90% of the property value, reducing the need for upfront payments.`,
    },
    {
      title: "Longer Repayment Tenure",
      description: ` With tenures extending up to 30 years, EMIs remain affordable and reduce financial burden.`,
    },
    {
      title: "Tax Benefits ",
      description: ` Home loan borrowers can claim deductions under. Section 80C: Up to ₹1.5 lakh deduction on principal repayment.
  Section 24(b): Up to ₹2 lakh deduction on interest paid annually.`,
    },
    {
      title: "Capital Appreciation ",
      description: ` Real estate value appreciates over time, making homeownership a good investment..`,
    },
  ];
  const points1 = [
    {
      title: "  Down Payment Requirement",
      description: `Borrowers must arrange 10-25% of the property’s cost, which can be a financial challenge.`,
    },
    {
      title: " Long-Term Commitment",
      description: `Since home loans extend up to 30 years, it requires consistent repayment discipline.`,
    },
    {
      title: "Property Hypothecation",
      description: `Until the loan is fully repaid, the bank holds rights over the property..`,
    },
    {
      title: "Processing Time & Documentation",
      description: `Home loan approval involves thorough verification, making it time-consuming compared to personal loans.`,
    },
  ];
  const points2 = [
    {
      title: "Opt for a Car Loan",
      description: ` If you want lower interest rates, longer repayment terms, and don’t mind your car being used as collateral.`,
    },
    {
      title: "Choose a Personal Loan",
      description: `If you want flexibility in usage, don’t want your car hypothecated, or need additional funds beyond just the vehicle cost.`,
    },
  ];
  const points3 = [
    {
      title: "Compare Interest Rates",
      points: [
        " Different banks and financial institutions offer varying home loan interest rates. A lower interest rate means lower EMIs and total repayment amount.",
      ],
    },
    {
      title: "Fixed vs. Floating Rate:",
      points: [
        "Fixed Interest Rate: Remains constant throughout the loan tenure, ensuring predictable EMIs.",
        "Floating Interest Rate: Varies based on market conditions, potentially lowering your repayment cost if interest rates decrease.",
        " Tip: If interest rates are expected to rise, a fixed-rate loan is a safer choice. If rates are likely to fall, a floating-rate loan is more beneficial.",
      ],
    },
    {
      title: "Loan Amount & Eligibility",
      points: [
        " Loan-to-Value (LTV) Ratio: Banks finance 75-90% of the property value, and you must arrange the rest as a down payment.",
        "Income & Credit Score: A higher credit score (750+) improves eligibility and helps secure better interest rates.",
        " Tip: Use a home loan eligibility calculator to estimate how much loan you can get based on your income and financial status.",
      ],
    },
    {
      title: "Loan Tenure & EMI Affordability",
      points: [
        " Longer tenure (up to 30 years) reduces EMI burden but increases total interest paid.",
        "Shorter tenure means higher EMIs but helps you clear debt faster with less interest cost.",
        " Tip: Choose a tenure that balances affordability with faster repayment to minimize overall interest outflow.",
      ],
    },
    {
      title: "Processing Fees & Other Charges",
      points: [
        "Processing Fees: Banks charge 0.25% - 1% of the loan amount as a processing fee, which adds to the total cost.",
        "Prepayment & Foreclosure Charges: Some lenders charge a penalty for repaying the loan before tenure ends.",
        "Tip: Opt for a lender with low or zero prepayment charges to reduce your financial burden in the future.",
      ],
    },
    {
      title: " Tax Benefits on Home Loan",
      points: [
        "Section 80C: Deduction of up to ₹1.5 lakh on principal repayment.",
        " Section 24(b): Deduction of up to ₹2 lakh on interest paid annually.",
        "Tip: Choose a loan structure that allows you to maximize tax benefits while ensuring affordability.",
      ],
    },
    {
      title: " Lender’s Reputation & Customer Service",
      points: [
        "Research the lender’s credibility based on customer reviews, complaint resolution efficiency, and loan servicing.",
        "Check for digital loan management features like online EMI tracking, customer support, and ease of documentation.",
        " Tip: Go for a lender with transparent policies, good customer support, and a hassle-free loan process.",
      ],
    },
    {
      title: " Special Offers & Government Schemes",
      points: [
        "Pradhan Mantri Awas Yojana (PMAY): First-time homebuyers can get interest subsidies up to ₹2.67 lakh under this scheme.",
        "Festive Offers: Many banks reduce interest rates and fees during festive seasons.",
        "Tip: Stay updated on seasonal discounts and government schemes to save costs.",
      ],
    },
  ];
  return (
    <>
      <Helmet>
        <title>Home Loan - Compare & Apply Lowest Interest Rate @8.50%</title>
        <meta
          name="description"
          content="Home Loan - Compare and Apply for the best housing loan offers from top banks/HFCs at low interest rates online. Home Loan upto 10 Cr. Check Eligibility."
        />
        <meta
          name="keywords"
          content="apply for home loan, home construction loans, home finance, housing finance companies, "
        />
        <link rel="canonical" href="https://thefinmaster.com/home_loan" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">Home Loan</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-4xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-0 pl-6">
                  <span className="text-blue-600 ">
                    Turn your dream home into a reality with fast Home Loans
                  </span>{" "}
                  low EMIs, simple process, and support every step of the way
                </h1>
                {/* <a href="#insurance_plans">
                      <button className="py-4 px-8  border rounded-full bg-blue-600 text-white shadow-lg hover:shadow-xl text-lg mt-10 font-medium">
                        See Plans
                      </button>
                    </a> */}
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full"
                src="/assets/home11.png"
                alt="home-loan"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="shadow-lg rounded-lg p-4 md:p-8 lg:p-12 mb-6 px-6 mx-4 md:mx-8 lg:mx-10 text-gray-600 flex justify-center items-center">
        <p className="text-lg md:text-base lg:text-lg text-center">
          Transforming your dream of homeownership into reality starts at
          TheFinMaster.com. Our platform simplifies the home loan search by
          offering a comprehensive selection from various reputable lenders.
          Easily compare interest rates, terms, and down payment requirements to
          find the perfect fit. Don't navigate this journey alone –
          TheFinMaster.com is your partner in unlocking the door to your dream
          home.{" "}
        </p>
      </div>
      <div className="container mx-auto">
      <div className="flex flex-col lg:flex-row">
      <div className="w-full lg:w-3/4">
      <div id="loan_plans" className="lg:pr-6 md:px-4 px-2">
      {loading ? (
              <div className="text-center py-10">Loading Loan options...</div>
            ) : error ? (
              <div className="text-center py-10 text-red-600">{error}</div>
            ) : loanData.length === 0 ? (
              <div className="text-center py-10">
                No car Loan options available at the moment.
              </div>
            ) : (
          loanData.map((loan) => (
            <div
              key={loan.id}
              className="md:w-full max-w-[900px] py-2 border shadow-lg overflow-hidden hover:shadow-xl rounded-lg flex flex-wrap md:flex-nowrap gap-5 justify-between mx-auto mt-4"
            >
              <div className="flex flex-wrap md:flex-nowrap w-full justify-center items-center md:justify-normal">
                <Link to={`/home_loan/${loan.page_url}`}>
                  <div className="w-[95%] md:w-[220px] overflow-hidden py-2 md:ml-2">
                    <img
                      className="w-full h-full object-contain"
                      alt={loan.page_title || "Loan Provider"}
                      src={loan.image}
                    />
                  </div>
                </Link>
              </div>

              <div className="w-full md:pl-0 pl-4 mt-6">
                <p className="text-gray-500 font-medium">Interest Rate</p>
                <h4 className="text-2xl py-0.5 font-bold">
                  {loan.interest_rate}%
                </h4>
              </div>

              <div className="w-full md:pl-0 pl-4 mt-6">
                <p className="text-gray-500 font-medium">Loan Amount </p>
                <h4 className="text-2xl py-0.5 font-bold">
                  ₹{loan.loan_amount}
                </h4>
              </div>

              <div className="w-full md:pl-0 pl-4 mt-6">
                <p className="text-gray-500 font-medium">Processing Fee</p>
                <h4 className="text-2xl py-0.5 font-bold">
                  ₹{loan.processing_fee}
                </h4>
              </div>

              <div className="flex flex-wrap justify-center md:justify-between items-center my-5 w-full gap-2 px-4">
                <a
                  href={loan.bank_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <button className="bg-green-500 text-white font-semibold py-2 px-4 rounded hover:bg-green-600 transition duration-300">
                    Get Quote!
                  </button>
                </a>

                <Link to={`/home_loan/${loan.page_url}`}>
                  <button className="text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-300 bg-blue-500">
                    See Details
                  </button>
                </Link>
              </div>
            </div>
          ))
        )}
         </div>
</div>
<div className="w-full lg:w-1/4 px-2 md:px-4 mt-6 lg:mt-0">
          <div className="sticky top-20">
            {/* Ad 1 - Visible on all screen sizes */}
            <a href="https://play.google.com/store/apps/details?id=com.epifi.paisa&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="block">
              <img 
                src="/assets/neoside.png" 
                alt="Insurance Special Offer" 
                className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              />
            </a>
            
            {/* Ad 2 - Hidden on small mobile, visible on larger screens */}
            {/* <a href="https://example.com/premium-discount" target="_blank" rel="noopener noreferrer" className="hidden sm:block mt-6">
              <img 
                src="/assets/niyoo.png" 
                alt="Premium Discount Offer" 
                className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              />
            </a> */}
            
            {/* Compact ad for mobile only */}
            {/* <a href="https://example.com/mobile-offer" target="_blank" rel="noopener noreferrer" className="block sm:hidden mt-4">
              <img 
                src="/assets/niyoo.png" 
                alt="Mobile Special Offer" 
                className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              />
            </a> */}
          </div>
        </div>
      </div>
    </div>
      <div className="flex justify-center items-center  p-4 mt-10">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center mt-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            What is a Home Loan?
          </h2>
          <p className="text-gray-600 leading-relaxed">
            A home loan is a financial product that allows individuals to borrow
            money from banks, non-banking financial companies (NBFCs), or
            housing finance institutions to purchase, construct, or renovate a
            residential property. The borrower repays the loan in Equated
            Monthly Installments (EMIs) over a predetermined tenure, which can
            range from 5 to 30 years.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Home loans are secured loans, meaning the purchased property itself
            serves as collateral. If the borrower defaults on repayment, the
            lender has the legal right to seize and sell the property to recover
            the outstanding loan amount.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800">
          Key stats of a Home Loan
        </h2>
        {/* <p className="text-gray-600 text-sm md:text-base mt-2">
          Choosing the right car insurance is crucial for financial security on
          the road. Here are the three main types of car insurance plans:
        </p> */}

        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Rising Home Loan Disbursements in India
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Public sector banks in India disbursed individual housing loans
              totaling ₹3.22 trillion, while housing finance companies disbursed
              ₹3.11 trillion, marking a 19% growth compared to the previous
              fiscal year.
            </p>
          </div>

          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📊</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Housing Loans as a Percentage of GDP
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              In 2023, housing loans constituted 12.3% of India's GDP. This
              figure is projected to rise to 13% by 2025, indicating a growing
              emphasis on housing finance in the country's economic framework.
            </p>
          </div>

          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Home Loan Disbursements
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              In the financial year 2023, Indian banks disbursed over ₹2.5
              trillion in housing loans, surpassing pre-COVID levels. This surge
              reflects a renewed interest among homebuyers, with more Indians
              investing in residential properties.
            </p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center">
          🏡 Types of Home Loans in India
        </h2>
        <p className="text-gray-700 text-base md:text-lg mt-3 text-center max-w-3xl mx-auto">
          Home loans in India come in various forms to cater to different
          housing and financial needs. Banks and financial institutions offer
          diverse home financing solutions, ensuring accessibility for various
          borrower requirements. Below are the primary types of home loans
          available in India:
        </p>

        {/* Responsive Grid Layout */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {loanTypes.map((loan, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
              className="bg-white shadow-md border border-gray-200 rounded-xl p-6 flex flex-col"
            >
              <div className="text-3xl text-blue-500">{loan.icon}</div>
              <h3 className="text-xl font-semibold mt-3">{loan.name}</h3>
              <p className="mt-2 text-gray-600">{loan.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center">
          🏡 Key Components of a Home Loan
        </h2>
        <p className="text-gray-700 text-lg mt-3 text-center max-w-3xl mx-auto">
          A home loan consists of several key components that determine the
          overall cost, repayment structure, and borrower obligations.
          Understanding these elements helps individuals make informed decisions
          when applying for a loan.
        </p>

        <div className="mt-8 space-y-4">
          {homeLoanComponents.map((component, index) => (
            <motion.div
              key={index}
              className="bg-white shadow-md border border-gray-200 rounded-lg p-5 cursor-pointer"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl text-blue-500">
                    {component.icon}
                  </span>
                  <h3 className="text-lg font-semibold">{component.title}</h3>
                </div>
                <span className="text-xl">
                  {openIndex === index ? "−" : "+"}
                </span>
              </div>

              {openIndex === index && (
                <motion.p
                  className="mt-3 text-gray-600 whitespace-pre-line"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.3 }}
                >
                  {component.description}
                </motion.p>
              )}
            </motion.div>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-12 space-y-8">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center">
          Eligibility Criteria for Availing a Home Loan
        </h2>
        <p className="text-gray-700 text-lg mt-3 text-center max-w-3xl mx-auto">
          Home loan eligibility is determined based on various factors,
          including the applicant's age, income, employment type, credit score,
          and residency status. Lenders assess these criteria to evaluate an
          individual’s repayment capability before approving the loan. Below are
          the key eligibility requirements:
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Age Criteria */}
          <div className="bg-white shadow-md border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-2 mb-2">
              <User className="text-blue-500" />
              <h3 className="font-semibold text-xl">Age Criteria</h3>
            </div>
            <ul className="list-disc ml-5 text-gray-600 space-y-1 text-md">
              <li>
                The applicant must be at least 18 years old at the time of
                application
              </li>
              <li>
                The maximum age limit is 70 years at the time of loan maturity.
              </li>
              <li>
                A younger applicant with a stable income is more likely to get a
                longer loan tenure and better interest rates.
              </li>
            </ul>
          </div>

          {/* Residency Status */}
          <div className="bg-white shadow-md border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-2 mb-2">
              <Home className="text-purple-500" />
              <h3 className="font-semibold text-lg">Residency Status</h3>
            </div>
            <ul className="list-disc ml-5 text-gray-600 space-y-1 text-md">
              <li>Resident Indians</li>
              <li>Non-Resident Indians (NRIs)</li>
              <li>
                Persons of Indian Origin (PIOs) Certain banks also have specific
                home loan products designed for NRIs and PIOs, with
                documentation requirements differing slightly from resident
                applicants.
              </li>
            </ul>
          </div>

          {/* Credit Score */}
          <div className="bg-white shadow-md border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="text-pink-500" />
              <h3 className="font-semibold text-lg">
                Credit Score & Financial History
              </h3>
            </div>
            <ul className="list-disc ml-5 text-gray-600 space-y-1 text-md">
              <li>
                A credit score of 750 or above is preferred by lenders as it
                indicates responsible credit behavior.
              </li>
              <li>
                Scores below 750 may result in higher interest rates or loan
                rejections.{" "}
              </li>
              <li>
                Lenders assess the applicant’s existing debts, repayment
                history, and financial discipline before approving the loan.
              </li>
            </ul>
          </div>

          {/* Property Requirements */}
          <div className="bg-white shadow-md border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="text-indigo-500" />
              <h3 className="font-semibold text-lg">
                Property & Residence Requirements
              </h3>
            </div>
            <ul className="list-disc ml-5 text-gray-600 space-y-1 text-md">
              <li>
                The applicant should have a permanent residence or a rented
                residence where they have lived for at least a year before
                applying.
              </li>
              <li>
                The property being purchased or constructed must be legally
                approved and fulfill all regulatory requirements.
              </li>
            </ul>
          </div>
          {/* Employment Type & Income */}
          <div className="bg-white shadow-md border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-2 mb-2">
              <BadgeCheck className="text-green-500" />
              <h3 className="font-semibold text-lg">
                {" "}
                Employment Type & Income Stability
              </h3>
            </div>
            <ul className="list-disc ml-5 text-gray-600 space-y-1 text-md">
              <li>
                Salaried Individuals: Employees of private or public sector
                companies with a stable income are eligible.
              </li>
              <li>
                Self-Employed Individuals: Business owners, entrepreneurs, or
                professionals (such as doctors, CAs, architects) with a
                consistent income can apply.
              </li>
              <li>
                Minimum annual income requirements vary across lenders,
                typically starting from ₹5-6 lakh per annum.
              </li>
              <li>
                A stable job or a business with regular earnings increases the
                chances of approval.
              </li>
            </ul>
          </div>
          {/* LTV & Down Payment */}
          <div className="bg-white shadow-md border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-2 mb-2">
              <Banknote className="text-yellow-500" />
              <h3 className="font-semibold text-lg">
                Loan-to-Value (LTV) Ratio & Down Payment
              </h3>
            </div>
            <ul className="list-disc ml-5 text-gray-600 space-y-1 text-md">
              <li>
                Lenders finance up to 75-90% of the property’s value depending
                on the loan amount and applicant profile.
              </li>
              <li>
                The applicant must arrange the remaining 10-25% as a down
                payment.
              </li>
              <li>
                A higher down payment can improve loan eligibility and reduce
                overall interest costs.
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-center mb-6">
          Why Choose a Home Loan?
        </h2>
        <p className="text-gray-600 text-center mb-8">
          When financing a property purchase, borrowers often choose between a
          home loan and other financing options such as personal loans or loan
          against property. While both options provide financial support, they
          differ in various aspects, making home loans a preferable choice for
          buying or constructing a house.
        </p>

        {/* Responsive Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse bg-white shadow-md rounded-lg overflow-hidden">
            {/* Table Header */}
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="p-4 text-left">Feature</th>
                <th className="p-4 text-left">Home Loan</th>
                <th className="p-4 text-left">Personal Loan</th>
                <th className="p-4 text-left">Loan Against Property</th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {/* Purpose */}
              <tr className="border-b hover:bg-gray-100">
                <td className="p-4 font-semibold">Purpose</td>
                <td className="p-4">
                  Buying, constructing, or renovating a house
                </td>
                <td className="p-4">
                  Any personal expense (medical, travel, wedding, etc.)
                </td>
                <td className="p-4">
                  Raising funds using property as collateral
                </td>
              </tr>

              {/* Collateral */}
              <tr className="border-b hover:bg-gray-100 bg-gray-50">
                <td className="p-4 font-semibold">Collateral</td>
                <td className="p-4">Property itself serves as collateral</td>
                <td className="p-4">Unsecured loan (no collateral)</td>
                <td className="p-4">Requires mortgaging property</td>
              </tr>

              {/* Loan Tenure */}
              <tr className="border-b hover:bg-gray-100">
                <td className="p-4 font-semibold">Loan Tenure</td>
                <td className="p-4">Up to 30 years</td>
                <td className="p-4">Usually 1-5 years</td>
                <td className="p-4">10-20 years</td>
              </tr>

              {/* Interest Rate */}
              <tr className="border-b hover:bg-gray-100 bg-gray-50">
                <td className="p-4 font-semibold">Interest Rate</td>
                <td className="p-4">8-10% (lower due to secured nature)</td>
                <td className="p-4">10-24% (higher due to risk)</td>
                <td className="p-4">9-15%</td>
              </tr>

              {/* Loan Amount */}
              <tr className="border-b hover:bg-gray-100">
                <td className="p-4 font-semibold">Loan Amount</td>
                <td className="p-4">75-90% of property value</td>
                <td className="p-4">Based on income & credit score</td>
                <td className="p-4">50-70% of property’s market value</td>
              </tr>

              {/* Tax Benefits */}
              <tr className="hover:bg-gray-100 bg-gray-50">
                <td className="p-4 font-semibold">Tax Benefits</td>
                <td className="p-4">
                  Eligible for tax benefits under Sections 80C & 24(b)
                </td>
                <td className="p-4">No tax benefits</td>
                <td className="p-4">Limited tax benefits in some cases</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-gray-600 text-center mb-8">
          From this comparison, it is evident that a home loan is the best
          option for purchasing property due to lower interest rates, longer
          repayment tenure, and tax benefits. Personal loans, although flexible,
          have higher interest rates and shorter repayment periods, making them
          less ideal for real estate investments.{" "}
        </p>
      </div>

      <div className=" px-2 md:px-6 bg-gray-100">
        <div className=" p-6 ">
          <h1 className="text-3xl font-bold text-blue-700 mb-4">
            Pros and Cons of Home Loans
          </h1>
          <p className="text-gray-700 mb-6">✅ Pros:</p>

          {points.map((point, index) => (
            <div
              key={index}
              className="mb-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <h2 className="text-xl font-semibold text-blue-600">
                {point.title}
              </h2>
              <p className="text-gray-600">{point.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className=" px-2 md:px-6 bg-gray-100">
        <div className=" p-6 ">
          <h1 className="text-3xl font-bold text-blue-700 mb-4">
            Pros and Cons of Home Loans
          </h1>
          <p className="text-gray-700 mb-6">❌ Cons</p>

          {points1.map((point, index) => (
            <div
              key={index}
              className="mb-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <h2 className="text-xl font-semibold text-blue-600">
                {point.title}
              </h2>
              <p className="text-gray-600">{point.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className=" px-2 md:px-6 bg-gray-100">
        <div className=" p-6 ">
          <h1 className="text-3xl font-bold text-blue-700 mb-4">
            Which Loan Should You Choose?
          </h1>
          {/* <p className="text-gray-700 mb-6">
            ❌ Cons of Car Loans
             </p> */}

          {points2.map((point, index) => (
            <div
              key={index}
              className="mb-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <h2 className="text-xl font-semibold text-blue-600">
                {point.title}
              </h2>
              <p className="text-gray-600">{point.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className=" px-2 p-6 md:px-6 bg-gray-100">
        <div className=" p-6 min-h-screen">
          <h1 className="text-3xl font-bold text-blue-700 mb-4">
            How to Choose the Right Home Loan: Key Factors to Consider
          </h1>
          <p className="text-gray-700 mb-6">
            Selecting the right home loan is crucial for managing your finances
            effectively and ensuring a hassle-free borrowing experience. Here
            are some key factors to consider when choosing the best home loan:{" "}
          </p>

          {/* Single Column Layout */}

          {points3.map((item, index) => (
            <div
              key={index}
              className="p-5 mb-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <h2 className="text-xl font-semibold text-blue-600">
                {item.title}
              </h2>
              <ul className="list-disc list-inside text-gray-600 mt-2">
                {item.points.map((point, idx) => (
                  <li key={idx} className="mt-1">
                    ✔ {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6 p-6">
        <h1 className="text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default HomeLoanNew;
